import { parseTime } from './clock.js';
import { estimateMinutes } from './beoGen.js';
import { POOLSETS } from '../data/poolsets.js';

/**
 * An event on the pool deck, put on tomorrow's board by a beat.
 *
 * The finale's pattern (`sim/story.js`, `finaleFor`): posting it remembers
 * which day it is for, and that day's board asks for it and gets a card. The
 * card is an ordinary card with its own grid on it, which is all the board,
 * the sheet and the setup need to know to put it on the deck rather than in
 * the ballroom. It never ghosts: an event nobody took out to the pool is an
 * event the hotel moved indoors, not one somebody else set.
 */

function clockText(minutes) {
  const wrapped = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(wrapped / 60)).padStart(2, '0')}:${String(wrapped % 60).padStart(2, '0')}`;
}

export function createPoolSets({ clock, grid, shiftFor = () => null, rungFor = () => 'cold', rows = POOLSETS }) {
  /** Which set is on which day's board. */
  let posted = {};

  const rowOf = (id) => rows.find((row) => row.id === id) ?? null;

  return {
    rows,

    /** From a beat: tomorrow's board carries it. */
    post(id) {
      const row = rowOf(id);
      if (!row || Object.values(posted).includes(id)) return false;
      posted[clock.day + 1] = id;
      return true;
    },

    /** The card for a day, if one was posted for it. */
    cardFor(day) {
      const row = rowOf(posted[day]);
      if (!row) return null;
      const shift = shiftFor(day);
      const start = parseTime(shift?.start ?? '14:00');
      const beo = {
        ...row,
        id: `${row.id}-${day}`,
        day,
        grid,
        setup: { ...row.setup, layout: { ...row.setup.layout }, staging: [...row.setup.staging] },
        shortfalls: row.shortfalls.map((entry) => ({ amount: 0, ...entry, resolved: false, via: null })),
        postedAt: clockText(start),
        setupBy: clockText(start + row.lead),
        doors: clockText(start + row.lead + row.doorsAfter),
        ghosts: false,
        works: [...row.works],
      };
      delete beo.lead;
      delete beo.doorsAfter;
      beo.estimate = estimateMinutes(beo, { rungFor });
      return beo;
    },

    /** Whether the event you have taken sends you somewhere. */
    works(zone, active) {
      return Boolean(active?.venue && active.works?.includes(zone));
    },

    get posted() {
      return { ...posted };
    },

    toJSON: () => ({ posted: { ...posted } }),
    load(state) {
      posted = { ...(state?.posted ?? {}) };
    },
    clear() {
      posted = {};
    },
  };
}
